import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, inject } from "vue";
import router from "@/router/index";
import { IQuestion, ISurvey } from "@/domain/entities/survey/survey.types";
import { useSurveyStore } from "@/stores/surveyStore";
import { useUserStore } from "@/stores/userStore";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import FooterButton from "@/components/shared/button/FooterButton.vue";
import Page from "@/components/shared/Page.vue";
import BaseTextarea from "@/components/shared/inputs/base/BaseTextarea.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  RouterName,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import { useServiceStore } from "@/stores/serviceStore";


const __default__ = {
  name: "WithDrawal",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const surveyStore = useSurveyStore();
const userStore = useUserStore();
const serviceStore = useServiceStore();
const withdrawalSurvey = ref<ISurvey>();

const serviceName = computed(
  () => serviceStore.serviceInfo?.name ?? "해피로드"
);

const isSurveyQuestionExisted = computed(
  () => withdrawalSurvey.value?.questions.length
);

const userAnswers = ref<Map<number, Map<string, string>>>(new Map());
const withdrawalAvailable = computed(() => {
  if (
    isSurveyFetchedComplete.value &&
    !withdrawalSurvey.value?.questions?.length
  )
    return true;

  const answerValues = [...userAnswers.value.values()];
  const selectedRadioValue = answerValues.find((answer) =>
    answer.get("selected")
  );
  const textareaValue = answerValues.find((answer) => answer.get("inputValue"));
  if (!selectedRadioValue) {
    return false;
  }
  if (
    selectedRadioValue.get("selected") === "ETC" &&
    !textareaValue?.get("inputValue")
  ) {
    return false;
  }
  return true;
});
const showWithdrawalSuccessModal = ref(false);

const onClickRadioButton = (answerId: string, question: IQuestion) => {
  const map = new Map<string, string>();
  map.set("selected", answerId);
  userAnswers.value.set(question.order, map);
  const textArea = document.getElementById(`textArea${question.order + 1}`) as
    | HTMLTextAreaElement
    | undefined;
  if (!textArea) {
    return;
  }
  if (answerId === "ETC") {
    textArea.focus();
    return;
  }
  textArea.value = "";
  textArea.blur();
  updateTextAreaAnswer(question.order + 1, textArea.value);
};

const focusRadioInput = (questionOrder: number) => {
  const radioInput = document.getElementById(`radio${questionOrder - 1}`) as
    | HTMLInputElement
    | undefined;
  if (radioInput) {
    radioInput.checked = true;
    const map = new Map<string, string>();
    map.set("selected", radioInput.value);
    userAnswers.value.set(questionOrder - 1, map);
  }
};

const updateTextAreaAnswer = (questionOrder: number, textareaValue: string) => {
  const map = new Map<string, string>();
  map.set("inputValue", textareaValue);
  userAnswers.value.set(questionOrder, map);
};

const getRadioButtonId = (questionOrder: number, answerId: string) => {
  if (answerId !== "ETC") {
    return undefined;
  }
  return `radio${questionOrder}`;
};

const showWithdrawalConfirmModal = ref(false);
const toggleShowWithdrawalConfirmModal = (show: boolean) => {
  showWithdrawalConfirmModal.value = show;
};
const onClickFooterButton = async () => {
  if (!withdrawalAvailable.value) return;
  toggleShowWithdrawalConfirmModal(true);
};

const requestWithdrawal = async () => {
  userAnswers.value = new Map([...userAnswers.value.entries()].sort());
  const withdrawalResult = await surveyStore.requestWitdrawal(
    withdrawalSurvey.value?.id ?? "",
    userAnswers.value
  );
  toggleShowWithdrawalConfirmModal(false);

  if (!withdrawalResult.isSuccess) {
    showErrorModal(
      "회원탈퇴 오류",
      withdrawalResult.errorMessage,
      withdrawalResult.resultCode,
      withdrawalResult.errorTime
    );
    return;
  }
  showWithdrawalSuccessModal.value = true;
};

const goToLogoutPage = () => {
  const isPublicLineApi = serviceStore.serviceInfo?.isPublicLineApi;
  const isPublicTalkApi = serviceStore.serviceInfo?.isPublicTalkApi;

  showToastMessage("탈퇴처리 되었습니다", 3500, 0);
  userStore.logout();
  router.push({
    name:
      isPublicLineApi || isPublicTalkApi
        ? RouterName.MainLogout
        : RouterName.Login,
  });
};

const getTextAreaLength = (questionOrder: number) => {
  if (!userAnswers.value.get(questionOrder)) {
    return 0;
  }
  return userAnswers.value.get(questionOrder)!.get("inputValue")!.length;
};

const isSurveyFetchedComplete = ref(false);

const getWithdrawalSurvey = async () => {
  const withdrawalSurveyResult =
    await surveyStore.getWithdrawalSurveyContents();
  isSurveyFetchedComplete.value = true;

  if (!withdrawalSurveyResult.data) return;

  withdrawalSurvey.value = withdrawalSurveyResult.data;
  withdrawalSurvey.value?.questions.forEach((question) => {
    if (question.answerType === "text") {
      const map = new Map<string, string>();
      map.set("inputValue", "");
      userAnswers.value.set(question.order, map);
    }
  });
};

onMounted(async () => {
  getWithdrawalSurvey();
});

const __returned__ = { showErrorModal, showToastMessage, surveyStore, userStore, serviceStore, withdrawalSurvey, serviceName, isSurveyQuestionExisted, userAnswers, withdrawalAvailable, showWithdrawalSuccessModal, onClickRadioButton, focusRadioInput, updateTextAreaAnswer, getRadioButtonId, showWithdrawalConfirmModal, toggleShowWithdrawalConfirmModal, onClickFooterButton, requestWithdrawal, goToLogoutPage, getTextAreaLength, isSurveyFetchedComplete, getWithdrawalSurvey, NavigationBar, FooterButton, Page, BaseTextarea, Modal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})